<template>
  <APageHead
    :title="$t('txt.10')"
    :sub="$t('txt.11')"
    icon="menu_book"
  >
    <template #actions>
      <template v-if="$route.query.barcode">
        <span class="et-version">
          <i class="material-icons-round et-version__icon">vertical_align_top</i>
          배포 버전: v<strong>{{ textbooksData.major_version }}.{{ textbooksData.minor_version }}</strong>
        </span>
        <APill :kind="statusPillKind">{{ statusLabel }}</APill>
        <!-- 승인·반려는 관리자 전용 (교재_분류값_관리자확정_백엔드답변.md §8) -->
        <ABtn
          v-if="canRequestApproval"
          kind="yellow"
          icon="send"
          @click="changeStatus('request')"
        >승인요청</ABtn>
        <ABtn
          v-if="currentStatus === 'request'"
          kind="ghost"
          icon="undo"
          @click="changeStatus('none')"
        >요청 취소</ABtn>
        <!--<ABtn kind="ghost" icon="upgrade" @click="minorVersionUp">Minor 버전업</ABtn>-->
      </template>
      <ABtn kind="primary" icon="save" @click="submitForm">{{ $t('btn.04') }}</ABtn>
      <ABtn
        v-if="$route.query.barcode"
        kind="danger"
        icon="delete"
        @click="deleteTextbook"
      >{{ $t('btn.11') }}</ABtn>
    </template>
  </APageHead>

  <div class="et-page">
    <VeeForm
      ref="formRef"
      :validation-schema="schema"
      @submit="onSubmit"
    >
      <div class="et-grid">
        <!-- 좌측: 커버 이미지 업로드 -->
        <ACard :padding="20" class="et-cover-card">
          <template #head>
            <p class="p-section-label">
              <i class="material-icons-round">image</i>
              커버 이미지
            </p>
          </template>

          <label for="upload-image" class="et-cover">
            <input
              id="upload-image"
              type="file"
              accept=".jpg,.jpeg,image/jpeg"
              hidden
              @change="getFileName($event.target.files)"
            />
            <img
              id="preview"
              :src="previewImage || defaultCover"
              alt="cover preview"
              class="et-cover__img"
              @error="onImageError"
            />
            <div class="et-cover__overlay">
              <i class="material-icons-round">photo_camera</i>
              <span>클릭하여 변경</span>
            </div>
          </label>

          <p class="et-cover__hint">
            <i class="material-icons-round">info</i>
            JPG 형식만 등록 가능 (.jpg, .jpeg / 최대 5MB)
          </p>
        </ACard>

        <!-- 우측: 교재 정보 -->
        <div class="et-right">
          <ACard :padding="20">
            <template #head>
              <p class="p-section-label">
                <i class="material-icons-round">menu_book</i>
                {{ $t('label.35') }}
              </p>
            </template>

            <div class="et-form-grid et-form-grid--4">
              <Field v-slot="{ field, errors }" name="barcode">
                <div class="p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round p-field__label-icon">qr_code_2</i>
                    {{ $t('label.28') }} <span class="p-field__req">*</span>
                  </label>
                  <input
                    type="text"
                    v-bind="field"
                    class="p-input"
                    :class="{ 'is-error': errors.length }"
                    :disabled="!!$route.query.barcode"
                    :placeholder="$t('label.28')"
                  />
                  <ErrorMessage name="barcode" v-slot="{ message }">
                    <div class="p-field__error">{{ message }}</div>
                  </ErrorMessage>
                </div>
              </Field>

              <Field v-slot="{ field, errors }" name="title">
                <div class="p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round p-field__label-icon">title</i>
                    {{ $t('label.14') }} <span class="p-field__req">*</span>
                  </label>
                  <input
                    type="text"
                    v-bind="field"
                    class="p-input"
                    :class="{ 'is-error': errors.length }"
                    :placeholder="$t('label.14')"
                  />
                  <ErrorMessage name="title" v-slot="{ message }">
                    <div class="p-field__error">{{ message }}</div>
                  </ErrorMessage>
                </div>
              </Field>

              <Field v-slot="{ field, errors }" name="publisher">
                <div class="p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round p-field__label-icon">business</i>
                    {{ $t('label.29') }} <span class="p-field__req">*</span>
                  </label>
                  <input
                    type="text"
                    v-bind="field"
                    class="p-input"
                    :class="{ 'is-error': errors.length }"
                    :placeholder="$t('label.29')"
                  />
                  <ErrorMessage name="publisher" v-slot="{ message }">
                    <div class="p-field__error">{{ message }}</div>
                  </ErrorMessage>
                </div>
              </Field>
            </div>

            <!-- 분류 정보는 관리자가 검수 단계에서 지정 (교재_분류값_관리자확정_백엔드답변.md §5) -->
            <div class="et-classify">
              <p class="et-classify__note">
                <i class="material-icons-round">info</i>
                분류 정보(도서분야 · 학습단계 · 테마 · 학습언어)는 관리자가 검수 단계에서 지정합니다.
                비워두고 저장하셔도 됩니다.
              </p>
              <div v-if="$route.query.barcode" class="et-classify__grid">
                <div
                  v-for="item in classificationItems"
                  :key="item.label"
                  class="et-classify__item"
                >
                  <span class="et-classify__label">{{ item.label }}</span>
                  <span v-if="item.values.length" class="et-classify__values">
                    <APill v-for="value in item.values" :key="value" kind="default">{{ value }}</APill>
                  </span>
                  <span v-else class="et-classify__empty">관리자 지정 예정</span>
                </div>
              </div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">notes</i>
                Description <span class="p-field__optional">(optional)</span>
              </label>
              <div class="et-editor-wrap">
                <QuillEditor v-model="textbooksData.description" :config="editorConfig" />
              </div>
            </div>
          </ACard>

          <!-- 판매/대여 가격: product_type 은 결제 조인 키라 저자 화면에서 제외 (백엔드답변 §7) -->
          <!--<ACard :padding="20">
            <template #head>
              <p class="p-section-label">
                <i class="material-icons-round">request_quote</i>
                {{ $t('label.34') }}
              </p>
            </template>

            <div class="et-form-grid et-form-grid--3 et-form-grid--end">
              <div class="p-field">
                <label class="p-field__label">
                  <i class="material-icons-round p-field__label-icon">label_important</i>
                  {{ $t('label.40') }}
                </label>
                <Field v-slot="{ field }" name="product_type">
                  <CustomSelect
                    :options="productTypeOptions"
                    :model-value="field.value"
                    placeholder="Select a product type"
                    @update:model-value="field.onInput"
                    @blur="field.onBlur"
                  />
                </Field>
                <ErrorMessage name="product_type" v-slot="{ message }">
                  <div class="p-field__error">{{ message }}</div>
                </ErrorMessage>
              </div>

              <div class="et-pricing-action">
                <ABtn kind="ghost" icon="visibility" @click.prevent="openModal">가격표 보기</ABtn>
              </div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">sell</i>
                Tags
              </label>
              <MultipleSelect
                id="tags-select"
                v-model="selectedTags"
                label=""
                :options="tagOptions"
                placeholder="Select tags"
                :allow-search="true"
                :multiple="true"
              />
            </div>
          </ACard>-->
        </div>
      </div>
    </VeeForm>

    <!-- 가격표 모달 -->
    <AModal v-model="isModalVisible" title="가격표" icon="request_quote" size="md">
      <ATable
        :columns="pricingCols"
        :rows="productPricing"
        empty-text="가격 정보가 없습니다"
        row-key="product_type"
      >
        <template #cell:list_price="{ row }">
          <span class="et-pricing-amount">{{ row.list_price }} {{ row.currency_code }}</span>
        </template>
      </ATable>

      <template #foot>
        <ABtn kind="ghost" @click="closeModal">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import QuillEditor from "@/components/QuillEditor.vue";
// 분류값 입력(CustomSelect)·태그(MultipleSelect) 는 저자 화면에서 제외됨 —
// 아래 주석 처리된 "판매/대여 가격" 카드를 되살릴 때 import 도 함께 복구해야 한다
import { Field, Form as VeeForm, ErrorMessage } from "vee-validate";
import Swal from "sweetalert2";

// 커버 이미지 제약 — 서버가 .jpg/.jpeg 만 허용한다
// ("Unsupported file extension. Only .jpeg, .jpg files are allowed.")
const COVER_EXTENSIONS = ['jpg', 'jpeg'];
const MAX_COVER_SIZE = 5 * 1024 * 1024;
import * as yup from "yup";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import ATable from "@/components/Pineapple/ATable.vue";

export default {
  name: "EditTextbook",
  components: {
    QuillEditor,
    Field, VeeForm, ErrorMessage,
    APageHead, ACard, ABtn, APill, AModal, ATable,
  },
  data() {
    return {
      textbooksData: {
        barcode: "",
        title: "",
        publisher: "",
        description: "",
        cover_image: "",
        editor_idx: "",
        major_version: "",
        minor_version: "",
      },
      // 관리자가 지정하는 분류값 — 화면에서는 읽기 전용으로만 보여준다
      classification: {
        fields: [],
        targetLanguages: [],
        grade: "",
        gradeLabel: "",
        theme: "",
        learningLanguage: "",
      },
      previewImage: "",
      defaultCover: require('@/assets/img/no_image.png'),
      editorConfig: {
        htmlView: true,
        minHeight: "300px",
        toolbarOptions: [
          ["bold", "italic"],
          [{ header: [1, 2, 3, false] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
        ],
      },
      schema: yup.object({
        barcode: yup
          .string().min(6, this.$t('alert.49', { obj: this.$t('label.28'), count: 6 }))
          .required(this.$t('alert.71', { obj: this.$t('label.28') })),
        title: yup
          .string().required(this.$t('alert.71', { obj: this.$t('label.14') })),
        publisher: yup
          .string().required(this.$t('alert.71', { obj: this.$t('label.29') })),
      }),
      currencyOptions: [],
      isModalVisible: false,
      productTypeOptions: [
        { label: "A type", value: "A" },
        { label: "B type", value: "B" },
        { label: "C type", value: "C" },
        { label: "D type", value: "D" },
        { label: "E type", value: "E" },
        { label: "F type", value: "F" },
        { label: "G type", value: "G" },
      ],
      productPricing: [],
      tagOptions: [
        { label: "TOPIK", value: "TOPIK" },
        { label: "회화", value: "회화" },
        { label: "문법", value: "문법" },
        { label: "어휘", value: "어휘" },
        { label: "취업", value: "취업" },
        { label: "유학", value: "유학" },
        { label: "여행", value: "여행" },
        { label: "비즈니스", value: "비즈니스" },
      ],
      selectedTags: [],
      // 인세 계약 조건 (화면 입력은 없지만 PATCH 시 현재 값을 그대로 되돌려 보내야 한다)
      // — 정산_프론트엔드개발_가이드.md §9.3
      royaltyInfo: {
        max_royalty_rate: null,
      },
      co_authors: [],
      currentStatus: 'none',
      pricingCols: [
        { key: 'product_type',        label: '타입',     align: 'center', width: 70 },
        { key: 'product_description', label: '설명',     align: 'left' },
        { key: 'list_price',          label: '가격',     align: 'right',  width: 130 },
        { key: 'store',               label: '스토어',   align: 'center', width: 100 },
      ],
    };
  },
  computed: {
    // 승인·반려는 관리자 전용, 저자는 승인요청과 그 취소만 (백엔드답변 §8)
    canRequestApproval() {
      return ['', 'none', 'rejection'].includes(this.currentStatus);
    },
    classificationItems() {
      const c = this.classification;
      return [
        { label: '도서분야',  values: c.fields.map(f => f.name).filter(Boolean) },
        { label: '학습단계',  values: [c.gradeLabel || c.grade].filter(Boolean) },
        { label: '테마',      values: [c.theme].filter(Boolean) },
        { label: '학습언어',  values: [c.learningLanguage].filter(Boolean) },
        { label: '대상언어',  values: c.targetLanguages.map(l => l.name).filter(Boolean) },
      ];
    },
    userInfo() {
      return this.$store.state.auth.user_info || {};
    },
    statusLabel() {
      const statusMap = {
        'request': '승인요청',
        'approval': '승인완료',
        'rejection': '거절',
        'none': '미요청',
        '': '미요청',
      };
      return statusMap[this.currentStatus] || '미요청';
    },
    statusPillKind() {
      const map = {
        'request': 'warn',
        'approval': 'ok',
        'rejection': 'err',
        'none': 'default',
        '': 'default',
      };
      return map[this.currentStatus] || 'default';
    },
  },
  mounted() {
    this.getProductPricing();

    const barcode = this.$route.query.barcode;
    if (barcode != undefined) {
      this.getTextbook(barcode);
    }
    if (this.$route.query.barcode) {
      this.getCoAuthor();
    }
  },
  methods: {
    submitForm() {
      // External 저장 버튼 → 폼 submit 트리거
      if (this.$refs.formRef && typeof this.$refs.formRef.$el?.requestSubmit === 'function') {
        this.$refs.formRef.$el.requestSubmit();
      } else if (this.$refs.formRef && typeof this.$refs.formRef.submit === 'function') {
        this.$refs.formRef.submit();
      } else {
        // fallback: dispatch native submit event
        const form = this.$refs.formRef?.$el;
        if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    },
    async getProductPricing() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/productPricing",
          loadingBar: true,
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.productPricing = res.data;
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    async getCoAuthor() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/editor/co-author",
          params: {
            barcode: this.$route.query.barcode,
            editor_idx: this.userInfo.idx,
          },
          loadingBar: true,
        });
        this.co_authors = res.data;
      } catch (error) {
        console.error("Error :", error);
      }
    },
    async getTextbook(barcode) {
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/editor/textbooks/${barcode}`,
          loadingBar: true,
        });
        if (res.status === 200) {
          const data = res.data;
          this.textbooksData = {
            barcode: data.barcode,
            title: data.title,
            publisher: data.publisher,
            description: data.description,
            cover_image: data.cover_image,
            major_version: data.major_version,
            minor_version: data.minor_version,
          };
          // 분류값은 관리자 지정 — 조회만 하고 저장 시 전송하지 않는다 (백엔드답변 §1·§5)
          this.classification = {
            fields: Array.isArray(data.fields) ? data.fields : [],
            targetLanguages: Array.isArray(data.target_languages) ? data.target_languages : [],
            grade: data.grade || "",
            gradeLabel: data.grade_label || "",
            theme: data.theme || "",
            learningLanguage: data.learning_language || "",
          };
          this.currentStatus = data.status || '';
          this.royaltyInfo.max_royalty_rate = data.max_royalty_rate ?? null;
          this.selectedTags = this.$stringToArr(data.tags);
          this.previewImage = data.cover_image ? `${data.cover_image}` : "";
          this.$refs.formRef.setValues(this.textbooksData);
        }
      } catch (error) {
        console.error("Error :", error);
        if (error.response) {
          const status = error.response.status;
          if (status === 403) return this.$accessDenied(this.$router, "접근하실 수 없습니다");
          if (status === 404) return this.$accessDenied(this.$router, "존재하지 않는 교재입니다");
          if (status === 500) return this.$accessDenied(this.$router, "서버 오류가 발생했습니다");
        }
        if (error.code === 'ERR_NETWORK') {
          return this.$accessDenied(this.$router, "서버에 연결할 수 없습니다");
        }
      }
    },
    async onSubmit(values, { resetForm }) {
      const requestFn = async () => {
        // 분류값(field_ids·grade·theme·learning_language)과 product_type 은 관리자가 지정한다.
        // 서버가 "키 없음 = 변경 없음"이므로 저자 화면에서는 아예 보내지 않는다 (백엔드답변 §4·§5·§7)
        if (!this.$route.query.barcode) {
          return await this.$axios({
            method: "post",
            url: "/textbook/editor/textbooks",
            data: {
              barcode: values.barcode?.trim() || values.barcode,
              title: values.title,
              publisher: values.publisher,
              description: this.textbooksData.description || "",
              tags: this.$arrToString(this.selectedTags),
              editor_idx: this.userInfo.idx,
            },
          });
        } else {
          const data = {
            barcode: values.barcode?.trim() || values.barcode,
            title: values.title,
            publisher: values.publisher,
            description: this.textbooksData.description || "",
            tags: this.$arrToString(this.selectedTags),
          };

          // 구버전 서버(운영 미배포)는 max_royalty_rate 를 빼고 보내면 도서별 상한을
          // 전역 상한(35)으로 되돌린다. 부분 수정 전환(백엔드답변 §4)이 운영에 배포되면
          // 이 블록은 제거해도 된다 — 그때까지는 방어용으로 현재 값을 그대로 실어 보낸다.
          // — 정산_프론트엔드개발_가이드.md §9.3
          const maxRate = Number(this.royaltyInfo.max_royalty_rate);
          if (this.royaltyInfo.max_royalty_rate !== null
            && this.royaltyInfo.max_royalty_rate !== ""
            && !Number.isNaN(maxRate)) {
            data.max_royalty_rate = maxRate;
          }

          return await this.$axios({
            method: "patch",
            url: `/textbook/editor/textbooks/${this.$route.query.barcode}`,
            data,
          });
        }
      };

      let result = await this.$showConfirmationAndSubmit(requestFn, resetForm, { title: this.$t('alert.47') });

      if (result === "success") {
        const uploaded = await this.uploadCoverImage(values.barcode?.trim() || values.barcode);
        // 업로드 실패 시에는 목록으로 넘기지 않는다 — 재시도할 수 있도록 화면에 머문다
        if (!uploaded) return;

        const page = this.$route.query.page;
        this.$router.push({ path: "textbooks", query: page ? { page } : {} });
      }
    },

    // 커버 이미지 업로드. 업로드할 파일이 없으면 true(진행 가능), 실패하면 false
    async uploadCoverImage(submittedBarcode) {
      const imageInput = document.getElementById("upload-image");
      const file = imageInput?.files?.[0];
      if (!file) return true;

      // 폼 상태가 아니라 라우트/전송값 기준으로 바코드를 잡는다
      const barcode = this.$route.query.barcode || submittedBarcode;
      if (!barcode) {
        this.$notifyVue({ title: this.$t('txt.12'), text: '바코드를 확인할 수 없습니다.', type: "error" });
        return false;
      }

      const invalid = this.validateCoverFile(file);
      if (invalid) {
        this.$notifyVue({ title: this.$t('txt.12'), text: invalid, type: "error" });
        return false;
      }

      const formData = new FormData();
      formData.append("cover_image", file);

      try {
        await this.$axios({
          method: "post",
          url: `/textbook/editor/textbooks/${barcode}/upload-cover`,
          data: formData,
          loadingBar: true,
        });
        return true;
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;
        const detail = typeof data?.detail === 'string' ? data.detail : JSON.stringify(data?.detail ?? data);
        console.error(
          `커버 이미지 업로드 실패 [${status}] ${detail} — ` +
          `barcode=${barcode}, file=${file.name} (${file.type}, ${file.size} bytes)`,
          error.response,
        );
        // 서버 메시지를 그대로 노출해야 원인(권한·용량·형식)을 알 수 있다
        this.$notifyVue({
          title: this.$t('txt.12'),
          text: detail
            ? `${detail}${status ? ` (${status})` : ''}`
            : `${this.$t('txt.13')}${status ? ` (${status})` : ''}`,
          type: "error",
        });
        return false;
      }
    },
    // 커버 이미지 제약 위반 시 사용자 메시지를 돌려준다 (문제 없으면 null)
    validateCoverFile(file) {
      const extension = (file.name.split('.').pop() || '').toLowerCase();
      if (!COVER_EXTENSIONS.includes(extension)) {
        return `JPG 이미지만 등록할 수 있습니다. (.jpg, .jpeg / 선택한 파일: .${extension || '알 수 없음'})`;
      }
      if (file.size > MAX_COVER_SIZE) {
        return `이미지 용량이 너무 큽니다. 5MB 이하로 올려주세요. (현재 ${(file.size / 1024 / 1024).toFixed(1)}MB)`;
      }
      return null;
    },

    async getFileName(files) {
      if (!files || !files[0]) return;

      // 저장까지 간 뒤 400 을 받지 않도록 선택 시점에 걸러낸다
      const invalid = this.validateCoverFile(files[0]);
      if (invalid) {
        this.$notifyVue({ title: this.$t('txt.12'), text: invalid, type: "error" });
        const imageInput = document.getElementById("upload-image");
        if (imageInput) imageInput.value = "";
        return;
      }

      this.fileName = files[0];
      await this.base64(this.fileName);
    },
    base64(file) {
      return new Promise(resolve => {
        let a = new FileReader();
        a.onload = e => {
          resolve(e.target.result);
          const previewImage = document.getElementById('preview');
          if (previewImage) previewImage.src = e.target.result;
          this.previewImage = e.target.result;
        };
        a.readAsDataURL(file);
      });
    },
    onImageError(e) {
      try { e.target.src = require('@/assets/img/no_image.png'); } catch (err) { /* ignore */ }
    },
    openModal() {
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    // 저자에게 허용된 전이는 승인요청(request)과 그 취소(none) 뿐이다 (백엔드답변 §8)
    async changeStatus(status) {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;
      const statusMessages = {
        'request': '승인요청 하시겠습니까?',
        'none': '승인요청을 취소하시겠습니까?',
      };
      const successMessages = {
        'request': '승인요청이 접수되었습니다.',
        'none': '승인요청이 취소되었습니다.',
      };

      const confirmation = await Swal.fire({
        title: statusMessages[status] || '상태를 변경하시겠습니까?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('btn.22'),
        cancelButtonText: this.$t('btn.23'),
      });
      if (!confirmation.isConfirmed) return;

      try {
        const res = await this.$axios({
          method: "patch",
          url: `/textbook/editor/postStatus/${barcode}/${status}`,
          loadingBar: true,
        });
        if (res.status === 200) {
          this.currentStatus = status;
          this.$notifyVue({
            title: '성공',
            text: successMessages[status] || '상태가 변경되었습니다.',
            type: 'success',
          });
        }
      } catch (error) {
        console.error("Error :", error);
        // 400·403 의 detail 은 사용자에게 그대로 보여줄 수 있는 문장이다 (백엔드답변 §6·§8)
        this.$notifyVue({
          title: '처리 실패',
          text: error.response?.data?.detail || this.$t('alert.50'),
          type: 'danger',
        });
      }
    },
    //async minorVersionUp() {
    //  const barcode = this.$route.query.barcode;
    //  if (!barcode) return;
    //  const requestFn = async () => {
    //    return await this.$axios({
    //      method: "post",
    //      url: `/textbook/editor/postStatus/${barcode}/minorVersionUp`,
    //      loadingBar: true,
    //    });
    //  };
    //  const result = await this.$showConfirmationAndSubmit(requestFn, null, {
    //    title: 'Minor 버전을 업데이트 하시겠습니까?',
    //    text: '검토/테스트용 버전이 생성됩니다.',
    //  });
    //  if (result === "success") {
    //    await this.getTextbook(barcode);
    //    this.$notifyVue({ title: '성공', text: 'Minor 버전이 업데이트되었습니다.', type: 'success' });
    //  }
    //},
    async deleteTextbook() {
      const barcode = this.$route.query.barcode;
      if (!barcode) return;
      const requestFn = async () => {
        return await this.$axios({
          method: "delete",
          url: `/textbook/editor/textbooks/${barcode}`,
          loadingBar: true,
        });
      };
      const result = await this.$showConfirmationAndSubmit(requestFn, null, {
        title: '삭제 하시겠습니까?',
        text: '삭제 하시겠습니까?',
      });
      if (result === "success") {
        this.$notifyVue({ title: '성공', text: '삭제되었습니다.', type: 'success' });
        const page = this.$route.query.page;
        this.$router.push({ path: "textbooks", query: page ? { page } : {} });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.et-page {
  padding: 20px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $p-font-body;
}

// Header version badge
.et-version {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: $p-ink-mid;

  strong {
    font-family: $p-font-mono;
    color: $p-ink;
    font-weight: 800;
  }
}

.et-version__icon {
  font-size: 14px !important;
  color: $p-point-deep;
}

// Body grid: cover (left) + info (right)
.et-grid {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.et-cover-card {
  position: sticky;
  top: 72px;
}

// Cover image uploader
.et-cover {
  position: relative;
  display: block;
  width: 100%;
  border-radius: $p-radius;
  overflow: hidden;
  cursor: pointer;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  aspect-ratio: 3 / 4;

  &:hover .et-cover__overlay { opacity: 1; }
}

.et-cover__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.et-cover__overlay {
  position: absolute;
  inset: 0;
  background: rgba(31, 27, 20, 0.6);
  color: $p-point;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.15s ease;
  font-size: 13px;
  font-weight: 700;

  i { font-size: 32px; }
}

.et-cover__hint {
  margin: 12px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: $p-ink-soft;

  i { font-size: 14px !important; color: $p-info; }
}

.et-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

// Form grid
.et-form-grid {
  display: grid;
  gap: 14px;
  margin-bottom: 8px;

  &--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  &--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  &--end { align-items: flex-end; }
}

// 분류 정보 (읽기 전용 - 관리자 지정)
.et-classify {
  margin: 4px 0 18px;
  padding: 14px 16px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
}

.et-classify__note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: $p-ink-mid;

  i {
    font-size: 16px !important;
    color: $p-point-deep;
    flex-shrink: 0;
  }
}

.et-classify__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid $p-line;
}

.et-classify__item {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.et-classify__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: $p-ink-soft;
}

.et-classify__values {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.et-classify__empty {
  font-size: 12px;
  color: $p-ink-softer;
}

.et-pricing-action {
  display: flex;
  align-items: flex-end;
  padding-bottom: 14px; // align with p-field height
}

.et-pricing-amount {
  font-family: $p-font-mono;
  font-weight: 700;
  color: $p-ink;
}

// Optional label hint
.p-field__optional {
  font-size: 11px;
  font-weight: 500;
  color: $p-ink-soft;
  margin-left: 4px;
}

// Editor wrapper (Checklist §6: 카드 톤 + 옐로 포커스)
.et-editor-wrap {
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  background: $p-panel;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: $p-point-deep;
    box-shadow: 0 0 0 3px $p-point-glow;
  }
}

@media (max-width: 1100px) {
  .et-grid { grid-template-columns: 1fr; }
  .et-cover-card { position: static; }
  .et-form-grid--3,
  .et-form-grid--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .et-form-grid--3,
  .et-form-grid--4 { grid-template-columns: 1fr; }
}
</style>
